import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, TrendingUp, Users, Timer, Target } from "lucide-react";

const BeforeAfterComparison = () => {
  const comparisons = [
    {
      business: "Home Services Company",
      location: "Manchester",
      before: {
        traffic: "200/month",
        conversion: "1.2%",
        leads: "2-3/month",
        loadTime: "5.8s",
        description: "Outdated design, slow loading, no mobile optimization"
      },
      after: {
        traffic: "1,850/month",
        conversion: "4.8%",
        leads: "88/month",
        loadTime: "0.9s",
        description: "Modern design, lightning fast, conversion-optimized"
      },
      metrics: [
        { icon: Users, label: "Traffic Increase", value: "+825%" },
        { icon: Target, label: "Conversion Rate", value: "+300%" },
        { icon: TrendingUp, label: "Monthly Leads", value: "+2,833%" },
        { icon: Timer, label: "Load Time", value: "-84%" }
      ]
    },
    {
      business: "Professional Services Firm",
      location: "London",
      before: {
        traffic: "450/month",
        conversion: "0.8%",
        leads: "3-4/month",
        loadTime: "6.2s",
        description: "Generic template, poor user experience, minimal SEO"
      },
      after: {
        traffic: "3,200/month",
        conversion: "5.2%",
        leads: "166/month",
        loadTime: "1.1s",
        description: "Custom design, optimized funnels, SEO-focused structure"
      },
      metrics: [
        { icon: Users, label: "Traffic Increase", value: "+611%" },
        { icon: Target, label: "Conversion Rate", value: "+550%" },
        { icon: TrendingUp, label: "Monthly Leads", value: "+4,050%" },
        { icon: Timer, label: "Load Time", value: "-82%" }
      ]
    }
  ];

  return (
    <section className="py-10 md:py-14 bg-gradient-to-br from-background via-muted/30 to-background">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Real <span className="text-secondary">Transformations</span>, Real Results
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              See how we transformed struggling websites into lead-generating machines
            </p>
          </div>

          <div className="space-y-12">
            {comparisons.map((comparison, index) => (
              <Card key={index} className="border-2 border-secondary/20 shadow-luxury overflow-hidden">
                <CardContent className="p-0">
                  {/* Business Info Header */}
                  <div className="bg-gradient-to-r from-primary to-primary/90 text-white p-6">
                    <h3 className="text-2xl font-bold mb-1">{comparison.business}</h3>
                    <p className="text-white/80">{comparison.location}</p>
                  </div>

                  {/* Before/After Grid */}
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* BEFORE */}
                    <div className="p-8 bg-muted/50">
                      <div className="inline-block bg-destructive/20 text-destructive px-4 py-2 rounded-full text-sm font-bold mb-6">
                        BEFORE
                      </div>
                      
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Monthly Traffic:</span>
                          <span className="font-bold text-destructive">{comparison.before.traffic}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Conversion Rate:</span>
                          <span className="font-bold text-destructive">{comparison.before.conversion}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Monthly Leads:</span>
                          <span className="font-bold text-destructive">{comparison.before.leads}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Load Time:</span>
                          <span className="font-bold text-destructive">{comparison.before.loadTime}</span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground italic border-l-4 border-destructive/50 pl-4">
                        {comparison.before.description}
                      </p>
                    </div>

                    {/* AFTER */}
                    <div className="p-8 bg-secondary/5 relative">
                      <div className="absolute top-1/2 -left-6 transform -translate-y-1/2 hidden md:block">
                        <div className="bg-secondary rounded-full p-3">
                          <ArrowRight className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      <div className="inline-block bg-secondary/20 text-secondary px-4 py-2 rounded-full text-sm font-bold mb-6">
                        AFTER
                      </div>
                      
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Monthly Traffic:</span>
                          <span className="font-bold text-secondary">{comparison.after.traffic}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Conversion Rate:</span>
                          <span className="font-bold text-secondary">{comparison.after.conversion}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Monthly Leads:</span>
                          <span className="font-bold text-secondary">{comparison.after.leads}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Load Time:</span>
                          <span className="font-bold text-secondary">{comparison.after.loadTime}</span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground italic border-l-4 border-secondary pl-4">
                        {comparison.after.description}
                      </p>
                    </div>
                  </div>

                  {/* Metrics Highlights */}
                  <div className="bg-gradient-to-r from-secondary/10 to-primary/10 p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {comparison.metrics.map((metric, idx) => (
                        <div key={idx} className="text-center">
                          <div className="flex items-center justify-center mb-2">
                            <metric.icon className="w-5 h-5 text-secondary" />
                          </div>
                          <div className="text-2xl font-bold text-secondary mb-1">
                            {metric.value}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {metric.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-muted-foreground mb-6">
              Ready to transform your website into a lead-generating machine?
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterComparison;